import moment from 'moment';
import {Types} from 'mongoose';
import {CrudEntityTypes} from '@/classes/clientOnly/permissionTreeResources/enums/CrudEntityTypes';
import {CourseManagerAPI} from '@/classes/api/course-manager.api.class';
import {StudentHandlerApi} from '@/classes/api/student-handler.api.class';
import {EmployeeHandlerAPI} from '@/classes/api/employee-handler.api.class';
import {CatalogueManagerApi} from '@/classes/api/catalogue-manager.api';
import {ToDoManagerApi} from '@/classes/api/to-do-manager.api.class';
import {CourseStates} from '@/enums/CourseStates.enum';
import {ApplicationState} from '@/enums/recruiter/ApplicationState.enum';
import {ProductDiscriminatorType} from '@/enums/catalogue-manager/ProductDiscriminatorType.enum';
import {Weekdays} from '@/helpers/dateHelpers/Weekdays.enum';
import {minutesToTime} from '@/helpers/dateHelpers/timeConversion.helper';
import {emptyContext, idOf, LOGGABLE_ENTITIES, QuickContext, QuickFact, textOf} from '@/components/app/search/searchQuickViewData';
import {canReadEntity, canReadType} from '@/components/app/search/searchAccess';
import {ScheduleCourse, seatsOf} from '@/components/app/search/searchSchedule';
import {loadTrainingFacts} from '@/components/app/search/searchTrainings';
import {loadLogSummary} from '@/components/app/search/searchQuickLogs';
import {PaletteHit} from '@/components/app/search/searchEntities';
import {
    canLoadSchedule,
    loadCourseSchedule,
    loadInstitutionSchedule,
    loadParentSchedule,
    loadStudentSchedule,
    loadTeacherSchedule,
    ScheduleChild,
} from '@/components/app/search/searchScheduleLoader';

const CACHE_TTL_MS = 120_000;
const cache = new Map<string, { time: number; context: QuickContext }>();

/** Number of documents of a find request with limit 1 (maxPage equals the count then) */
function countOf(resp: { maxPage: number } | undefined, items: unknown[] | undefined): number {
    if (!resp || !items || !items.length) {
        return 0;
    }
    return resp.maxPage || items.length;
}

function activeCourseFilter() {
    return {courseState: [CourseStates.ACTIVE], endsAfter: new Date()};
}

async function countActiveCourses(filter: Record<string, unknown>): Promise<number | null> {
    if (!canReadType(CrudEntityTypes.COURSE, 'courseState', 'endDate', ...Object.keys(filter))) {
        return null;
    }
    const resp = await CourseManagerAPI.find({filter: {...filter, ...activeCourseFilter()}, limit: 1, fields: ['_id']} as any);
    return countOf(resp, resp?.courses);
}

async function loadStudent(id: Types.ObjectId, hit: PaletteHit, ctx: QuickContext) {
    const tasks: Array<Promise<void>> = [];
    tasks.push(loadStudentSchedule(id).catch(() => null).then((schedule) => {
        ctx.schedule = schedule;
    }));
    const hasParents = Array.isArray(hit.preview?.parents) && (hit.preview.parents as unknown[]).length > 0;
    if (!hasParents && canReadEntity(CrudEntityTypes.STUDENT, id, 'parents') && canReadType(CrudEntityTypes.PARENT, 'firstName', 'lastName')) {
        const withContact = canReadType(CrudEntityTypes.PARENT, 'contactInformation');
        tasks.push(StudentHandlerApi.findById(id, {
            fields: ['parents'],
            populate: {parents: {fields: withContact ? ['firstName', 'lastName', 'contactInformation'] : ['firstName', 'lastName']}},
        } as any).then((student) => {
            for (const parent of (student?.parents || []) as any[]) {
                const info = parent.contactInformation || {};
                const phone = textOf(info.mobile1 || info.phone1);
                if (phone) {
                    ctx.phones.push(phone);
                }
                if (info.mail1) {
                    ctx.mails.push(textOf(info.mail1));
                }
                ctx.facts.push({label: 'Eltern', value: [textOf(parent), phone].filter((el) => el).join(' · '), to: `/parents/${idOf(parent)}`});
            }
        }));
    }
    await Promise.all(tasks);
}

async function loadParent(id: Types.ObjectId, ctx: QuickContext) {
    if (!canReadType(CrudEntityTypes.STUDENT, 'parents', 'firstName', 'lastName')) {
        return;
    }
    // isActive only when readable (children are then shown without the "inaktiv" hint)
    const fields = canReadType(CrudEntityTypes.STUDENT, 'isActive') ? ['firstName', 'lastName', 'isActive'] : ['firstName', 'lastName'];
    const resp = await StudentHandlerApi.find({filter: {parents: [id]}, fields, limit: 10} as any);
    const students = resp?.students || [];
    const children: ScheduleChild[] = students.map((student) => ({
        id: String(student._id),
        firstName: textOf(student.firstName),
        name: textOf(student),
        isActive: student.isActive !== false,
    }));
    ctx.schedule = await loadParentSchedule(children).catch(() => null);
    if (!ctx.schedule) {
        children.forEach((child) => ctx.facts.push({
            label: 'Kind',
            value: `${child.name}${child.isActive ? '' : ' (inaktiv)'}`,
            to: `/students/${child.id}`,
        }));
    }
}

async function loadTeacherLike(id: Types.ObjectId, hit: PaletteHit, ctx: QuickContext) {
    const tasks: Array<Promise<void>> = [];
    if (canReadEntity(CrudEntityTypes.EMPLOYEE, id, 'assignedInstitutions')) {
        tasks.push(EmployeeHandlerAPI.findById(id, {fields: ['assignedInstitutions']} as any).then((employee) => {
            const count = (employee?.assignedInstitutions || []).length;
            // Assignment in the profile, not the locations of the courses (those are in the week plan)
            ctx.facts.push({label: 'Zugeteilte Standorte', value: count ? String(count) : 'keine'});
        }));
    }
    if (canLoadSchedule('teacher')) {
        tasks.push(loadTeacherSchedule(id, hit.entity === 'teachers' || hit.preview?.isTeacher === true).catch(() => null).then((schedule) => {
            ctx.schedule = schedule;
        }));
    } else {
        tasks.push(countActiveCourses({teacher: [id]}).then((count) => {
            if (count !== null) {
                ctx.facts.push({label: 'Kurse', value: count === 1 ? '1 aktiver Kurs' : `${count} aktive Kurse`});
            }
        }));
    }
    await Promise.all(tasks);
}

function nextAppointmentText(appointments: any[] | undefined): string {
    const next = (appointments || [])
        .filter((el) => !el.isCanceled && moment(el.appointmentDate).isAfter(moment()))
        .sort((a, b) => moment(a.appointmentDate).valueOf() - moment(b.appointmentDate).valueOf())[0];
    if (!next) {
        return '';
    }
    return `${moment(next.appointmentDate).format('dd, DD.MM., HH:mm')} · ${textOf(next.serviceName) || 'Bookings'}`;
}

async function loadApplicant(id: Types.ObjectId, ctx: QuickContext) {
    if (!canReadEntity(CrudEntityTypes.EMPLOYEE, id, 'vanillaApplication')) {
        return;
    }
    const withDocuments = canReadEntity(CrudEntityTypes.EMPLOYEE, id, 'missingDocumentTypes') && canReadType(CrudEntityTypes.DOCUMENT_TYPE, 'name');
    const employee = await EmployeeHandlerAPI.findById(id, {
        fields: withDocuments ? ['vanillaApplication', 'missingDocumentTypes'] : ['vanillaApplication'],
        populate: {
            'vanillaApplication.applicationProgressState': {fields: ['name']},
            ...(withDocuments ? {missingDocumentTypes: {fields: ['name']}} : {}),
        },
    } as any);
    const application = employee?.vanillaApplication as any;
    if (application) {
        const state = ApplicationState.Record[application.applicationState as ApplicationState]?.text || textOf(application.applicationState);
        const progress = textOf(application.applicationProgressState);
        ctx.facts.push({label: 'Bewerbung', value: [state, progress].filter((el) => el).join(' › ')});
        ctx.facts.push({label: 'Gespräch', value: nextAppointmentText(application.appointments) || 'kein Termin gebucht'});
    }
    const missing = employee?.missingDocumentTypes as unknown[] | undefined;
    if (Array.isArray(missing)) {
        ctx.facts.push({label: 'Unterlagen', value: missing.length ? `fehlt: ${missing.map(textOf).filter((el) => el).join(', ') || missing.length}` : 'vollständig'});
    }
}

async function loadInstitution(id: Types.ObjectId, ctx: QuickContext) {
    const tasks: Array<Promise<void>> = [];
    if (canReadType(CrudEntityTypes.EMPLOYEE, 'assignedInstitutions', 'isActive')) {
        tasks.push(EmployeeHandlerAPI.find({filter: {assignedInstitutions: [id], isActive: true}, limit: 1, fields: ['_id']} as any).then((resp) => {
            const count = countOf(resp, resp?.employees);
            ctx.facts.push({label: 'Lehrkräfte', value: count === 1 ? '1 zugeteilt' : `${count} zugeteilt`});
        }));
    }
    if (canLoadSchedule('institution')) {
        tasks.push(loadInstitutionSchedule(id).catch(() => null).then((schedule) => {
            ctx.schedule = schedule;
        }));
    } else {
        tasks.push(countActiveCourses({institution: [id]}).then((count) => {
            if (count !== null) {
                ctx.facts.push({label: 'Kurse', value: count === 1 ? '1 aktiver Kurs' : `${count} aktive Kurse`});
            }
        }));
    }
    await Promise.all(tasks);
}

async function loadCourse(id: Types.ObjectId, ctx: QuickContext) {
    if (!canReadEntity(CrudEntityTypes.COURSE, id, 'dayOfWeek', 'timeRange')) {
        return;
    }
    const fields = ['dayOfWeek', 'timeRange', 'subject', 'institution', 'teacher', 'room', 'studentItems', 'courseState']
        .filter((field) => canReadEntity(CrudEntityTypes.COURSE, id, field));
    const populate: Record<string, { fields: string[] }> = {};
    const targets: Array<[string, CrudEntityTypes, string[]]> = [
        ['subject', CrudEntityTypes.SUBJECT, ['name']],
        ['institution', CrudEntityTypes.INSTITUTION, ['name']],
        ['room', CrudEntityTypes.ROOM, ['name']],
        ['teacher', CrudEntityTypes.EMPLOYEE, ['firstName', 'lastName']],
    ];
    targets.filter(([field, type, names]) => fields.includes(field) && canReadType(type, ...names))
        .forEach(([field, , names]) => {
            populate[field] = {fields: names};
        });
    const course = await CourseManagerAPI.findById(id, {fields, populate} as any) as any;
    if (!course) {
        return;
    }
    const range = course.timeRange || {};
    const day = typeof course.dayOfWeek === 'number' ? Weekdays.Record[course.dayOfWeek as Weekdays]?.text : '';
    if (typeof range.startTime === 'number') {
        ctx.facts.push({label: 'Zeit', value: `${day} ${minutesToTime(range.startTime)}–${minutesToTime(range.endTime || range.startTime)}`.trim()});
    }
    if (course.subject) {
        ctx.facts.push({label: 'Fach', value: textOf(course.subject)});
    }
    const institutionId = idOf(course.institution);
    if (course.institution) {
        ctx.facts.push({label: 'Standort', value: textOf(course.institution), to: institutionId ? `/institutions/${institutionId}` : undefined});
    }
    if (institutionId) {
        ctx.links.push({label: 'Standort öffnen', icon: 'mdi-domain', to: `/institutions/${institutionId}`});
    }
    const teacherId = idOf(course.teacher);
    if (course.teacher) {
        ctx.facts.push({label: 'Lehrkraft', value: textOf(course.teacher), to: teacherId ? `/teachers/${teacherId}` : undefined});
    }
    if (textOf(course.room)) {
        ctx.facts.push({label: 'Raum', value: textOf(course.room)});
    }
    const seats = seatsOf(course);
    if (seats) {
        ctx.facts.push({label: 'Kinder', value: seats.text});
    }
    if (course.courseState) {
        ctx.facts.push({label: 'Status', value: CourseStates.Record[course.courseState as CourseStates]?.text || textOf(course.courseState)});
    }
    const teacherObjectId = teacherId && Types.ObjectId.isValid(teacherId) ? new Types.ObjectId(teacherId) : null;
    ctx.schedule = await loadCourseSchedule(course as ScheduleCourse, teacherObjectId).catch(() => null);
}

async function loadProduct(id: Types.ObjectId, ctx: QuickContext) {
    if (!canReadEntity(CrudEntityTypes.PRODUCT, id, 'name')) {
        return;
    }
    const fields = ['name', 'customId', 'type', 'grossPrice', 'isActive'].filter((field) => canReadEntity(CrudEntityTypes.PRODUCT, id, field));
    const product = await CatalogueManagerApi.product.findById(id, {fields} as any) as any;
    if (!product) {
        return;
    }
    if (product.type) {
        ctx.facts.push({label: 'Art', value: ProductDiscriminatorType.Record[product.type as ProductDiscriminatorType]?.text || textOf(product.type)});
    }
    if (typeof product.grossPrice === 'number') {
        ctx.facts.push({label: 'Preis (brutto)', value: product.grossPrice.toCurrencyString()});
    }
    if (typeof product.isActive === 'boolean') {
        ctx.facts.push({label: 'Status', value: product.isActive ? 'Aktiv' : 'Inaktiv'});
    }
}

/** Number of open to-dos of the profile */
async function loadOpenToDos(id: Types.ObjectId): Promise<QuickFact | null> {
    if (!canReadType(CrudEntityTypes.TO_DO_ITEM, 'entity', 'completed')) {
        return null;
    }
    const resp = await ToDoManagerApi.items.find({filter: {entity: [id], completed: false}, limit: 1, fields: ['_id']} as any);
    const count = countOf(resp, resp?.items);
    return {label: 'Offene To-dos', value: count ? String(count) : 'keine', to: count ? '/todos' : undefined};
}

async function loadEntityFacts(id: Types.ObjectId, hit: PaletteHit, ctx: QuickContext) {
    switch (hit.entity) {
        case 'students':
            return loadStudent(id, hit, ctx);
        case 'parents':
            return loadParent(id, ctx);
        case 'employees':
        case 'teachers':
            return loadTeacherLike(id, hit, ctx);
        case 'applicants':
            return loadApplicant(id, ctx);
        case 'institutions':
            return loadInstitution(id, ctx);
        case 'courses':
            return loadCourse(id, ctx);
        case 'products':
            return loadProduct(id, ctx);
        case 'trainings':
            return loadTrainingFacts(id, hit.preview || {}, ctx);
    }
}

/**
 * Loads additional context of a hit with the existing services, only for data the user may read.
 * Failures of single requests are ignored; results are cached for two minutes.
 */
export async function loadQuickContext(hit: PaletteHit): Promise<QuickContext> {
    const key = `${hit.entity}:${hit.id}`;
    const cached = cache.get(key);
    if (cached && Date.now() - cached.time < CACHE_TTL_MS) {
        return cached.context;
    }
    const ctx = emptyContext();
    if (!Types.ObjectId.isValid(hit.id)) {
        return ctx;
    }
    const id = new Types.ObjectId(hit.id);
    const extra: { lastContact: QuickFact | null; openToDos: QuickFact | null } = {lastContact: null, openToDos: null};
    const tasks: Array<Promise<unknown>> = [loadEntityFacts(id, hit, ctx).catch(() => undefined)];
    if (LOGGABLE_ENTITIES.includes(hit.entity)) {
        tasks.push(loadLogSummary(id, hit).then((summary) => {
            extra.lastContact = summary ? summary.lastContact : null;
            ctx.logs = summary ? summary.logs : [];
        }).catch(() => undefined));
        tasks.push(loadOpenToDos(id).then((fact) => {
            extra.openToDos = fact;
        }).catch(() => undefined));
    }
    await Promise.all(tasks);
    const header = [extra.lastContact, extra.openToDos].filter((el): el is QuickFact => !!el);
    ctx.facts = [...header, ...ctx.facts];
    cache.set(key, {time: Date.now(), context: ctx});
    return ctx;
}
